<template>
  <div class="full-data-panel p-6 space-y-8">
    <!-- 广告系列设置 -->
    <div
      id="campaign-section"
      class="bg-white rounded-xl border border-gray-200 p-6"
    >
      <CampaignDetail
        ref="campaignDetail"
        :data="data"
        :currency="currency"
        :enumConstants="enumConstants"
        :currentAccount="currentAccount"
        @update:name="handleCampaignNameChange"
        @update:data="handleCampaignDataChange"
        @update:campaignTraceConfig="handleCampaignTraceConfigChange"
      />
    </div>

    <!-- 广告组预览 -->
    <div class="space-y-6">
      <div class="flex items-center space-x-2">
        <svg
          class="w-5 h-5 text-purple-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          ></path>
        </svg>
        <span class="text-base font-medium text-gray-800">广告组预览</span>
        <span class="text-xs text-gray-400">Total {{ adGroupsCount }}</span>
      </div>

      <!-- 广告组列表 -->
      <div class="space-y-4">
        <div
          v-for="(adGroup, groupIndex) in data.adGroups || []"
          :key="groupIndex"
          :id="`adgroup-${groupIndex}-section`"
          class="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <!-- 广告组头部 -->
          <div
            class="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
            @click="toggleAdGroup(groupIndex)"
          >
            <div class="flex items-center space-x-3 flex-1">
              <div
                class="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center font-medium"
              >
                {{ groupIndex + 1 }}
              </div>
              <div
                class="w-full flex-1"
                @click.stop
              >
                <el-form
                  :ref="`adGroupNameForm_${groupIndex}`"
                  :model="adGroup"
                  :rules="getAdGroupNameRules(groupIndex)"
                  class="adgroup-name-form"
                  @submit.native.prevent
                >
                  <el-form-item
                    prop="name"
                    class="mb-0"
                  >
                    <el-input
                      :value="adGroup.name"
                      placeholder="请输入广告组名称"
                      @input="(val) => handleAdGroupNameChange(groupIndex, val)"
                      @blur="() => validateAdGroupName(groupIndex)"
                    ></el-input>
                  </el-form-item>
                </el-form>
                <div class="text-xs text-gray-400 mt-2">
                  <span> {{ adGroup.ads ? adGroup.ads.length : 0 }} 个广告</span>
                  <span> 出价类型：{{ getBidStrategyLabel(adGroup) }}</span>
                  <span v-if="['TARGET_CPA', 'TARGET_ROAS'].includes(adGroup.bidStrategy) && adGroup.bidAmount">
                    出价值:{{ formatBidAmount(adGroup.bidAmount) }}</span
                  >
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-4">
              <div class="flex items-center space-x-4">
                <div
                  class="flex items-center space-x-2"
                  @click.stop
                >
                  <span class="text-xs text-gray-500">状态</span>
                  <el-switch
                    :value="adGroup.status === 'ENABLED'"
                    active-color="#8b5cf6"
                    inactive-color="#d1d5db"
                    @change="(val) => handleAdGroupStatusChange(groupIndex, val ? 'ENABLED' : 'PAUSED')"
                  />
                </div>
                <el-button
                  type="text"
                  size="mini"
                  class="text-red-500 hover:text-red-700"
                  :disabled="!canDeleteAdGroup"
                  @click.stop="handleDeleteAdGroup(groupIndex)"
                >
                  <i class="el-icon-delete"></i> 删除
                </el-button>
              </div>
              <i
                class="el-icon-arrow-down"
                :class="{ 'rotate-180': expandedGroups.includes(groupIndex) }"
              ></i>
            </div>
          </div>

          <!-- 广告组内容 -->
          <div
            v-show="expandedGroups.includes(groupIndex)"
            class="p-6"
          >
            <AdGroupDetail
              :ref="`adGroupDetail_${groupIndex}`"
              :data="adGroup"
              :currency="currency"
              :adGroupIndex="groupIndex"
              :currentAccount="currentAccount"
              :enumConstants="enumConstants"
              :promotionLink="data.promoteInfo && data.promoteInfo.promotionLink"
              @update:name="handleAdGroupNameChange"
              @update:ad-name="handleAdNameChange"
              @update:ad-status="handleAdStatusChange"
              @update:optimize-position="handleOptimizePositionChange"
              @update:bid="handleBidChange"
              @delete:ad="handleDeleteAd"
              @ad-selected="handleAdSelected"
            />
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div
        v-if="!data.adGroups || data.adGroups.length === 0"
        class="bg-white rounded-xl border border-gray-200 p-12 text-center"
      >
        <svg
          class="w-16 h-16 mx-auto mb-4 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          ></path>
        </svg>
        <div class="text-gray-500">暂无广告组</div>
      </div>
    </div>

    <!-- 选中的广告详情弹窗 -->
    <el-dialog
      :visible.sync="showAdDetail"
      title="广告详情"
      width="800px"
      :close-on-click-modal="true"
    >
      <AdDetail
        v-if="selectedAd"
        :data="selectedAd"
        :adGroupData="selectedAdGroup"
        :currency="currency"
        :canDelete="canDeleteSelectedAd"
        @update:name="handleDialogAdNameChange"
        @update:status="handleDialogAdStatusChange"
        @delete="handleDialogDeleteAd"
      />
    </el-dialog>
  </div>
</template>

<script>
import CampaignDetail from './CampaignDetail.vue';
import AdGroupDetail from './AdGroupDetail.vue';
import AdDetail from './AdDetail.vue';

export default {
  name: 'FullDataPanel',
  components: {
    CampaignDetail,
    AdGroupDetail,
    AdDetail,
  },
  props: {
    data: {
      type: Object,
      default: () => ({ name: '', adGroups: [] }),
    },
    currency: {
      type: String,
      default: 'USD',
    },
    activeSection: {
      type: String,
      default: 'campaign',
    },
    enumConstants: {
      type: Object,
      default: () => ({}),
    },
    currentAccount: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    // 广告组名称重复校验器
    const validateAdGroupNameUnique = (rule, value, callback) => {
      if (!value || value.trim() === '') {
        callback(new Error('广告组名称不能为空'));
        return;
      }
      // 获取当前广告组索引
      const currentIndex = rule.adGroupIndex;
      const adGroups = this.data.adGroups || [];
      // 检查同一系列下是否有重复的广告组名称
      const duplicateIndex = adGroups.findIndex((group, index) => {
        return index !== currentIndex && group.name === value.trim();
      });
      if (duplicateIndex !== -1) {
        callback(new Error('同一系列下广告组名称不能重复'));
      } else {
        callback();
      }
    };

    return {
      expandedGroups: [0],
      showAdDetail: false,
      selectedAd: null,
      selectedAdGroup: null,
      selectedGroupIndex: -1,
      selectedAdIndex: -1,
      validateAdGroupNameUnique,
    };
  },
  computed: {
    adGroupsCount() {
      return this.data.adGroups ? this.data.adGroups.length : 0;
    },
    canDeleteAdGroup() {
      return this.data.adGroups && this.data.adGroups.length > 1;
    },
    canDeleteSelectedAd() {
      return this.selectedAdGroup && this.selectedAdGroup.ads && this.selectedAdGroup.ads.length > 1;
    },
  },
  watch: {
    activeSection: {
      handler(newSection) {
        this.handleSectionChange(newSection);
      },
      immediate: true,
    },
  },
  methods: {
    toggleAdGroup(index) {
      const idx = this.expandedGroups.indexOf(index);
      if (idx > -1) {
        this.expandedGroups.splice(idx, 1);
      } else {
        this.expandedGroups.push(index);
      }
    },
    // 获取广告组名称校验规则（带重复校验）
    getAdGroupNameRules(groupIndex) {
      return {
        name: [
          { required: true, message: '广告组名称不能为空', trigger: ['blur', 'change'] },
          { max: 375, message: '广告组名称长度不能超过375字符', trigger: ['blur', 'change'] },
          { validator: this.validateAdGroupNameUnique, adGroupIndex: groupIndex, trigger: ['blur', 'change'] },
        ],
      };
    },
    // 手动触发广告组名称校验
    validateAdGroupName(groupIndex) {
      const nameFormRef = `adGroupNameForm_${groupIndex}`;
      const nameFormArr = this.$refs[nameFormRef];
      const nameForm = Array.isArray(nameFormArr) ? nameFormArr[0] : nameFormArr;
      if (nameForm && nameForm.validate) {
        nameForm.validate().catch(() => {});
      }
    },
    // 广告系列名称修改
    handleCampaignNameChange(value) {
      this.$emit('update-campaign', { field: 'name', value });
    },
    // 广告系列数据修改（预算、出价等）
    handleCampaignDataChange(val) {
      // 将整个数据对象的变更传递给父组件
      this.$emit('update-campaign-data', val);
    },
    // 广告系列跟踪配置修改
    handleCampaignTraceConfigChange(val) {
      this.$emit('update-campaign', { field: 'campaignTraceConfig', value: val });
    },

    // 广告组名称修改
    handleAdGroupNameChange(groupIndex, value) {
      this.$emit('update-adgroup', { groupIndex, field: 'name', value });
    },
    // 广告组状态修改
    handleAdGroupStatusChange(groupIndex, value) {
      this.$emit('update-adgroup', { groupIndex, field: 'status', value });
    },
    // 删除广告组
    handleDeleteAdGroup(groupIndex) {
      if (!this.canDeleteAdGroup) {
        this.$message.warning('至少保留一个广告组');
        return;
      }
      this.$confirm('确定要删除这个广告组吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.$emit('delete-adgroup', groupIndex);
        })
        .catch(() => {});
    },
    // 广告名称修改
    handleAdNameChange(groupIndex, adIndex, value) {
      this.$emit('update-ad', { groupIndex, adIndex, field: 'name', value });
    },
    // 广告状态修改
    handleAdStatusChange(groupIndex, adIndex, value) {
      this.$emit('update-ad', { groupIndex, adIndex, field: 'status', value });
    },
    // 优化型定位修改
    handleOptimizePositionChange(groupIndex, val) {
      // 同时更新 optimizedTargetingEnabled 和 excludeDemographicExpansion
      this.$emit('update-adgroup', {
        groupIndex,
        field: 'optimizedTargetingEnabled',
        value: val.optimizedTargetingEnabled,
      });
      this.$emit('update-adgroup', {
        groupIndex,
        field: 'excludeDemographicExpansion',
        value: val.excludeDemographicExpansion,
      });
    },
    // 出价修改
    handleBidChange(groupIndex, val) {
      this.$emit('update-adgroup', { groupIndex, field: 'bidStrategy', value: val.bidStrategy });
      this.$emit('update-adgroup', { groupIndex, field: 'bidAmount', value: val.bidAmount });
    },
    // 删除广告
    handleDeleteAd(groupIndex, adIndex) {
      const adGroup = this.data.adGroups[groupIndex];
      if (!adGroup || !adGroup.ads || adGroup.ads.length <= 1) {
        this.$message.warning('广告组下至少保留一个广告');
        return;
      }
      this.$confirm('确定要删除这个广告吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.$emit('delete-ad', { groupIndex, adIndex });
        })
        .catch(() => {});
    },
    // 选中广告
    handleAdSelected(groupIndex, adIndex) {
      const adGroup = this.data.adGroups[groupIndex];
      if (adGroup && adGroup.ads && adGroup.ads[adIndex]) {
        this.selectedGroupIndex = groupIndex;
        this.selectedAdIndex = adIndex;
        this.selectedAdGroup = adGroup;
        this.selectedAd = adGroup.ads[adIndex];
        this.showAdDetail = true;
      }
    },

    // 弹窗内广告名称修改
    handleDialogAdNameChange(value) {
      if (this.selectedGroupIndex >= 0 && this.selectedAdIndex >= 0) {
        this.$emit('update-ad', {
          groupIndex: this.selectedGroupIndex,
          adIndex: this.selectedAdIndex,
          field: 'name',
          value,
        });
        this.selectedAd = { ...this.selectedAd, name: value };
      }
    },
    // 弹窗内广告状态修改
    handleDialogAdStatusChange(value) {
      if (this.selectedGroupIndex >= 0 && this.selectedAdIndex >= 0) {
        this.$emit('update-ad', {
          groupIndex: this.selectedGroupIndex,
          adIndex: this.selectedAdIndex,
          field: 'status',
          value,
        });
        this.selectedAd = { ...this.selectedAd, status: value };
      }
    },
    // 弹窗内删除广告
    handleDialogDeleteAd() {
      if (!this.canDeleteSelectedAd) {
        this.$message.warning('广告组下至少保留一个广告');
        return;
      }
      this.$confirm('确定要删除这个广告吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.$emit('delete-ad', { groupIndex: this.selectedGroupIndex, adIndex: this.selectedAdIndex });
          this.showAdDetail = false;
        })
        .catch(() => {});
    },
    handleSectionChange(section) {
      if (section === 'campaign') {
        this.scrollToCampaign();
      } else if (section.startsWith('adGroup-')) {
        const groupIndex = parseInt(section.split('-')[1]);
        this.scrollToAdGroup(groupIndex);
        if (!this.expandedGroups.includes(groupIndex)) {
          this.expandedGroups.push(groupIndex);
        }
      }
    },
    scrollToCampaign() {
      this.$nextTick(() => {
        const element = document.getElementById('campaign-section');
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    scrollToAdGroup(groupIndex) {
      this.$nextTick(() => {
        const element = document.getElementById(`adgroup-${groupIndex}-section`);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    getAdGroupDateRange(adGroup) {
      const start = adGroup.startDate || '12.15';
      const end = adGroup.endDate || '10:26';
      return `${start}-${end}`;
    },
    // 获取出价策略标签
    getBidStrategyLabel(adGroup) {
      const labelMap = {
        MAX_CONVERSION: '自动出价（目标每次转化费用）',
        TARGET_CPA: '目标每次转化费用',
        MAX_CONVERSION_VALUE: '自动出价（目标 ROAS）',
        TARGET_ROAS: '目标 ROAS',
      };
      // 广告组自身有出价策略时，使用广告组的
      if (adGroup.bidStrategy && labelMap[adGroup.bidStrategy]) {
        return labelMap[adGroup.bidStrategy];
      }
      // 否则使用系列级别的
      const campaignBidStrategy = adGroup.campaignBidStrategy || this.data.bidStrategy;
      return labelMap[campaignBidStrategy] || '广告系列出价';
    },
    // 格式化出价金额
    formatBidAmount(amount) {
      if (!amount && amount !== 0) return '';
      const currencySymbol = this.getCurrencySymbol();
      return `${currencySymbol}${Number(amount).toFixed(2)}`;
    },
    // 获取货币符号
    getCurrencySymbol() {
      const symbolMap = {
        USD: '$',
        CNY: '¥',
        EUR: '€',
        GBP: '£',
        JPY: '¥',
      };
      return symbolMap[this.currency] || this.currency + ' ';
    },
    // 对外提供的验证方法
    validate() {
      const promises = [];
      // 校验广告系列详情
      if (this.$refs.campaignDetail && this.$refs.campaignDetail.validate) {
        promises.push(this.$refs.campaignDetail.validate());
      }
      // 校验所有广告组名称表单
      const adGroups = this.data.adGroups || [];
      for (let i = 0; i < adGroups.length; i++) {
        // 校验广告组名称
        // 注意：v-for 内的动态 ref 返回的是数组
        const nameFormRef = `adGroupNameForm_${i}`;
        const nameFormArr = this.$refs[nameFormRef];
        const nameForm = Array.isArray(nameFormArr) ? nameFormArr[0] : nameFormArr;
        if (nameForm && nameForm.validate) {
          promises.push(nameForm.validate());
        }
        // 校验广告组详情
        const detailRefName = `adGroupDetail_${i}`;
        const adGroupRefArr = this.$refs[detailRefName];
        const adGroupRef = Array.isArray(adGroupRefArr) ? adGroupRefArr[0] : adGroupRefArr;
        if (adGroupRef && adGroupRef.validate) {
          promises.push(adGroupRef.validate());
        }
      }
      if (promises.length === 0) {
        return Promise.resolve(true);
      }
      return Promise.all(promises);
    },
  },
};
</script>

<style scoped>
.full-data-panel {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 自定义滚动条样式 */
.full-data-panel::-webkit-scrollbar {
  width: 6px;
}

.full-data-panel::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.full-data-panel::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.full-data-panel::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
